import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography } from '@mui/material';
import { Plus } from 'lucide-react';
import CommonTable from '../../common_components/CommonTable';
import Pagination from '../../common_components/Pagination';
import TableToolbar from '../../common_components/TableToolbar';
import AppButton from '../../common_components/AppButton';
import PageContainer from '../../common_components/PageContainer';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import { toast } from '../../common_components/Toast/ToastUtils';
import { fetchReports, deleteReportThunk, fetchJobStatus, generateDeckThunk, downloadDeckBlob, selectReports, selectReportsLoading, selectReportsTotal, selectReportsLastUpdated, selectDeleting, selectGeneratingDeckReportId, } from '../../redux/metaAdAuditSlice';
import { setActiveModuleKey } from '../../redux/userDataSlice';
import AddReportDrawer from './components/AddReportDrawer';
import ActionMenu from './components/ActionMenu';
/**
 * Meta Ad Audit — list page (Batch 6 rewrite).
 *
 * Changes from earlier batches:
 *   • NEW column "Report Name" (label) — first sortable column
 *   • Column order now: Report Name | Client Name | Duration | Results
 *                       | Created At | Updated At | Action
 *   • "Breakdown" column relabeled to "Results"
 *   • Sort is now server-side: handleSortChange resets to page 1 and re-fetches
 *   • Removed the buggy client-side sortedRows useMemo
 */
const MODULE_KEY = 'meta_ad_audit';
const ITEMS_PER_PAGE = 20;
// ── Formatters ───────────────────────────────────────────────────────────
const fmtDate = (iso) => {
    if (!iso)
        return '—';
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    if (isNaN(d.getTime()))
        return '—';
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};
const fmtDuration = (start, end) => `${fmtDate(start)} — ${fmtDate(end)}`;
// ── Columns (new order) ──────────────────────────────────────────────────
const COLUMNS = [
    { id: 'label', label: 'Report Name', width: '320px', sortable: true, align: 'left' },
    { id: 'duration', label: 'Duration', width: '240px', sortable: false, align: 'left' },
    { id: 'counts', label: 'Results', width: '200px', sortable: false, align: 'left' },
    { id: 'created_at', label: 'Created At', width: '150px', sortable: true, align: 'left' },
    { id: 'updated_at', label: 'Updated At', width: '150px', sortable: true, align: 'left' },
    { id: 'action', label: 'Action', width: '90px', sortable: false, align: 'center' },
];
// ── Status pill for queued/processing rows ───────────────────────────────
const JobStatusPill = ({ status }) => {
    const label = status === 'queued' ? 'Queued' :
        status === 'processing' ? 'Processing…' :
            status === 'failed' ? 'Failed' : 'Done';
    const bg = status === 'failed' ? '#FEF3F2' :
        status === 'done' ? '#ECFDF3' : '#FFF8E1';
    const color = status === 'failed' ? '#B42318' :
        status === 'done' ? '#027A48' : '#93370D';
    return (_jsx(Box, { sx: {
            display: 'inline-flex', alignItems: 'center',
            px: 1, py: 0.25, borderRadius: 999,
            bgcolor: bg, color: color,
            fontSize: 11, fontWeight: 600,
            ml: 1,
        }, children: label }));
};
// ── Page component ───────────────────────────────────────────────────────
const MetaAdAuditList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const reports = useSelector(selectReports);
    const loading = useSelector(selectReportsLoading);
    const total = useSelector(selectReportsTotal);
    const lastUpdated = useSelector(selectReportsLastUpdated);
    const deleting = useSelector(selectDeleting);
    const generatingDeckReportId = useSelector(selectGeneratingDeckReportId);
    const permissions = useSelector((state) => state.auth.permissions);
    const authToken = useSelector((s) => s?.auth?.token);
    const modulePerms = permissions?.[MODULE_KEY];
    const canAddReport = Array.isArray(modulePerms?.actions)
        ? modulePerms.actions.includes('add_report')
        : false;
    const canGenerateDeck = Array.isArray(modulePerms?.actions)
        ? modulePerms.actions.includes('generate_deck')
        : false;
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        columnId: 'created_at',
        direction: 'desc',
    });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const isFirstLoad = useRef(true);
    // ── Module-key registration ─────────────────────────────────────────
    useEffect(() => {
        dispatch(setActiveModuleKey(MODULE_KEY));
    }, [dispatch]);
    // ── Server-side fetch (now includes sort) ──────────────────────────
    const loadReports = useCallback(async () => {
        try {
            await dispatch(fetchReports({
                page: currentPage,
                limit: ITEMS_PER_PAGE,
                search: searchTerm || undefined,
                sort: sortConfig?.columnId,
                order: sortConfig?.direction,
            })).unwrap();
        }
        finally {
            isFirstLoad.current = false;
        }
    }, [dispatch, currentPage, searchTerm, sortConfig]);
    useEffect(() => {
        loadReports();
    }, [loadReports]);
    // ── Job polling for queued/processing rows ──────────────────────────
    const pollRef = useRef(null);
    useEffect(() => {
        const pending = reports.filter((r) => r.job?.status === 'queued' || r.job?.status === 'processing');
        if (!pending.length) {
            if (pollRef.current) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return;
        }
        if (pollRef.current)
            return;
        pollRef.current = window.setInterval(() => {
            pending.forEach((r) => dispatch(fetchJobStatus(r.report_id)));
        }, 4000);
        return () => {
            if (pollRef.current) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [reports, dispatch]);
    // ── Handlers ────────────────────────────────────────────────────────
    const handleAddNew = () => {
        setEditData(null);
        setDrawerOpen(true);
    };
    const handleEdit = (row) => {
        setEditData(row);
        setDrawerOpen(true);
    };
    const handleView = (row) => {
        navigate(`/meta-ad-audit/report/${row.report_id}`);
    };
    const handleDeleteOpen = (row) => setDeleteTarget(row);
    const handleDeleteConfirm = async () => {
        if (!deleteTarget)
            return;
        try {
            const res = await dispatch(deleteReportThunk(deleteTarget.report_id)).unwrap();
            toast.success(res.message || 'Report deleted');
            setDeleteTarget(null);
            loadReports();
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : 'Delete failed');
        }
    };
    const handleGenerateDeck = async (row) => {
        try {
            toast.info('Building deck… this takes ~5 seconds.');
            const result = await dispatch(generateDeckThunk({
                reportId: row.report_id,
                regenerate: row.deck?.generated_at
                    ? new Date(row.updated_at) > new Date(row.deck.generated_at)
                    : false,
            })).unwrap();
            toast.success(result.cached ? 'Deck ready — downloading…' : 'Deck generated — downloading…');
            await downloadDeckBlob(row.report_id, row.client_name, authToken || '');
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : err?.message || 'Deck generation failed');
        }
    };
    // ── Sort change → reset to page 1, refetch (server-side sort) ──────
    const handleSortChange = useCallback((columnId, direction) => {
        setCurrentPage(1);
        if (!direction) {
            setSortConfig(null);
            return;
        }
        setSortConfig({ columnId, direction });
    }, []);
    // ── Cell renderer ───────────────────────────────────────────────────
    const renderCell = (row, col) => {
        switch (col.id) {
            case 'label':
                return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', minWidth: 0, gap: 0.25 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: {
                                        fontSize: '14px !important',
                                        fontWeight: 600,
                                        color: '#101828',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }, title: row.label || row.report_id, children: row.label || '(unnamed)' }), row.job?.status && row.job.status !== 'done' && (_jsx(JobStatusPill, { status: row.job.status }))] }), _jsx(Typography, { sx: {
                                fontSize: 11,
                                color: '#475467',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }, children: row.client_name })] }));
            case 'duration':
                return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#475467' }, children: fmtDuration(row.period_start, row.period_end) }));
            case 'counts': {
                const c = row.counts || {};
                const total = row.ads_count || 0;
                return (_jsxs(Box, { sx: { display: 'flex', gap: 0.5, flexWrap: 'wrap', fontSize: 11 }, children: [_jsx(Pill, { color: "#027A48", bg: "#ECFDF3", label: `${c.Pass || 0} Pass` }), _jsx(Pill, { color: "#B54708", bg: "#FEF7C3", label: `${c.Optimize || 0} Opt` }), _jsx(Pill, { color: "#B42318", bg: "#FEF3F2", label: `${c.Fail || 0} Fail` }), _jsx(Pill, { color: "#475467", bg: "#F2F4F7", label: `${total} total` })] }));
            }
            case 'created_at':
                return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#475467' }, children: fmtDate(row.created_at) }));
            case 'updated_at':
                return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#475467' }, children: fmtDate(row.updated_at) }));
            case 'action':
                return (_jsx(ActionMenu, { row: row, canAddReport: canAddReport, canGenerateDeck: canGenerateDeck, deckBusy: generatingDeckReportId === row.report_id, onView: handleView, onEdit: handleEdit, onDelete: handleDeleteOpen, onGenerateDeck: handleGenerateDeck }));
            default:
                return null;
        }
    };
    // ── Render ──────────────────────────────────────────────────────────
    return (_jsxs(PageContainer, { children: [_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: _jsxs(Paper, { elevation: 0, sx: {
                        border: '1px solid #D0D5DD',
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 'calc(100vh - 180px)',
                        height: 'calc(100vh - 180px)'
                    }, children: [_jsxs(Box, { sx: {
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 1,
                                p: 0,
                                borderBottom: '1px solid #EAECF0',
                            }, children: [_jsx(TableToolbar, { title: "", showSearch: true, onSearchChange: (val) => {
                                        setSearchTerm(val);
                                        setCurrentPage(1);
                                    }, showRows: true, showExpand: false, showFullScreen: true }), canAddReport && (_jsx(AppButton, { variant: "contained", startIcon: _jsx(Plus, { size: 16 }), onClick: handleAddNew, className: "all-Font-Sizes", sx: {
                                        bgcolor: '#EE2A7B',
                                        textTransform: 'none',
                                        mr: 1,
                                        '&:hover': { bgcolor: '#D91F6A' },
                                    }, children: "Add New Report" }))] }), _jsx(Box, { sx: {
                                overflow: 'auto',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                '&::-webkit-scrollbar': { display: 'none' },
                            }, className: "table-meta-audit", children: _jsx(CommonTable, { columns: COLUMNS, rows: reports, sortConfig: sortConfig, onSortChange: handleSortChange, isLoading: loading, isFirstLoad: isFirstLoad.current, emptyMessage: "No reports yet \u2014 click \u201CAdd New Report\u201D to generate your first audit.", renderCell: (row, col) => renderCell(row, col), stickyHeader: true, expandTrigger: 'none', maxHeight: "550px", pinnedColumns: { left: [], right: ['action'] } }) }), !loading && total > 0 && (_jsx(Box, { sx: { p: 2, borderTop: '1px solid #EAECF0' }, children: _jsx(Pagination, { currentPage: currentPage, totalPages: Math.ceil(total / ITEMS_PER_PAGE), onPageChange: setCurrentPage, maxVisiblePages: 5, totalEntries: total, itemsPerPage: ITEMS_PER_PAGE, showEntriesMessage: true }) }))] }) }), _jsx(AddReportDrawer, { open: drawerOpen, onClose: () => {
                    setDrawerOpen(false);
                    setEditData(null);
                }, onSuccess: loadReports, editData: editData }), _jsx(ConfirmActionModal, { open: !!deleteTarget, title: "Delete Report", description: deleteTarget
                    ? `Are you sure you want to delete the Meta Ad Audit Report "${deleteTarget.label || deleteTarget.report_id}" for ${deleteTarget.client_name}? This cannot be undone.`
                    : '', confirmText: "Delete", onConfirm: handleDeleteConfirm, onClose: () => setDeleteTarget(null), loading: deleting, isDeleteIcon: true })] }));
};
const Pill = ({ color, bg, label, }) => (_jsx(Box, { sx: {
        px: 0.75,
        py: 0.25,
        borderRadius: 999,
        bgcolor: bg,
        color: color,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
    }, children: label }));
export default MetaAdAuditList;
